import { z } from "zod";
import { AVATARS } from "../domain/avatars";
import { matchHistoryPageSchema, userStatsSchema } from "./stats";

/**
 * Display-name length bounds. A nickname is a SHOWN LABEL, not a unique identifier, so it
 * is NOT restricted to a username-style character set. Any script (Persian, Latin),
 * spaces, and emoji are welcome (like Telegram's display-name field); only SAFETY limits
 * apply (see {@link sanitizeNickname} + {@link nicknameSchema}).
 */
export const NICKNAME_MIN = 1;
export const NICKNAME_MAX = 64;

/**
 * True for a code point that must be STRIPPED from a display name (a layout/spoofing
 * hazard, NOT content): C0/C1 control chars, the zero-width space + BOM, the bidi
 * controls (LRM/RLM marks, the LRE..RLO embeddings/overrides, the isolates), and the
 * line/paragraph separators. KEEPS ZWNJ (0x200C, the Persian half-space) and ZWJ
 * (0x200D, emoji sequences), and every letter/emoji/punctuation.
 */
function isHazardCodePoint(cp: number): boolean {
  return (
    cp <= 0x1f ||
    (cp >= 0x7f && cp <= 0x9f) ||
    cp === 0x200b ||
    cp === 0x200e ||
    cp === 0x200f ||
    (cp >= 0x202a && cp <= 0x202e) ||
    cp === 0x2028 ||
    cp === 0x2029 ||
    (cp >= 0x2066 && cp <= 0x2069) ||
    cp === 0xfeff
  );
}

/**
 * Normalize a raw display name for storage/validation: drop the hazard code points
 * above (iterating by CODE POINT so emoji survive intact), collapse any run of
 * whitespace (incl. exotic Unicode spaces) to a single ASCII space, and trim. Content
 * (letters/emoji/punctuation) is otherwise untouched.
 */
export function sanitizeNickname(raw: string): string {
  let out = "";
  for (const ch of raw) {
    if (!isHazardCodePoint(ch.codePointAt(0) ?? 0)) out += ch;
  }
  return out.replace(/\s+/gu, " ").trim();
}

/**
 * Display-name rules — a nickname is a shown DISPLAY LABEL, NOT a unique identifier, so
 * it allows ALL characters (any script, spaces, emoji, symbols) and the ONLY limit is
 * the length. It is sanitized first (invisible/RTL layout hazards stripped, whitespace
 * collapsed + trimmed) then checked ONLY for: non-empty / not whitespace-only, and a
 * 64-code-point cap. No character-set restriction, no profanity screen, no uniqueness
 * (all deliberately unlike the strict, unique username).
 */
export const nicknameSchema = z
  .string()
  .transform(sanitizeNickname)
  .pipe(
    z
      .string()
      .min(NICKNAME_MIN, "Display name can't be empty")
      .refine((val) => [...val].length <= NICKNAME_MAX, "Display name is too long"),
  );

/** App-username length bounds (Telegram-style). */
export const APP_USERNAME_MIN = 5;
export const APP_USERNAME_MAX = 32;

/**
 * The editable public handle — validated exactly like a Telegram username: 5–32
 * characters, only `[a-z0-9_]`, must start with a letter. It is case-INSENSITIVE
 * (stored lowercased for race-free unique lookups), so the schema normalizes to
 * lowercase after validating. Distinct from the (private, read-only) Telegram
 * username.
 */
export const APP_USERNAME_REGEX = /^[a-zA-Z][a-zA-Z0-9_]{4,31}$/;
export const appUsernameSchema = z
  .string()
  .trim()
  .regex(
    APP_USERNAME_REGEX,
    "Username must be 5–32 characters, start with a letter, and use only letters, numbers, or underscores",
  )
  .transform((s) => s.toLowerCase());

/** Partial profile edit: any subset of the editable fields. */
export const updateProfileSchema = z
  .object({
    // A display label: a valid (sanitized) name, OR `null` to CLEAR the custom name
    // and revert to the Telegram first+last name everywhere (via displayNameOf). An
    // empty / whitespace-only / invisible-only string ALSO means "clear" (normalized
    // to null first, so a cleared field never trips the non-empty rule). Omitted (the
    // field is optional via `.partial()`) = leave it unchanged.
    nickname: z.preprocess(
      (v) => (typeof v === "string" && sanitizeNickname(v) === "" ? null : v),
      nicknameSchema.nullable(),
    ),
    avatarId: z.enum(AVATARS),
    appUsername: appUsernameSchema,
    // Privacy toggles — hide match history / stats+trophies from OTHER users' profile view.
    hideHistory: z.boolean(),
    hideStats: z.boolean(),
  })
  .partial();
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

/** Toggle the per-user notifications preference. */
export const updateNotificationsSchema = z.object({
  enabled: z.boolean(),
});
export type UpdateNotificationsInput = z.infer<typeof updateNotificationsSchema>;

/** Result of an app-username availability check. When unavailable, `reason` says why
 *  (`taken` = held by someone else, `reserved` = a platform/system handle) so the client
 *  can show the right Persian message. */
export const usernameAvailabilitySchema = z.object({
  username: z.string(),
  available: z.boolean(),
  reason: z.enum(["taken", "reserved"]).optional(),
});
export type UsernameAvailability = z.infer<typeof usernameAvailabilitySchema>;

/**
 * Another user's public-facing profile. NEVER carries the Telegram IDENTIFIERS
 * (`telegramId` / the Telegram `username`) — those are private to the owner. Consumers
 * (lobby / table / friends) resolve the real avatar via `avatarPhotoId` + the
 * authed `GET /avatar/:id` endpoint, falling back to the preset `avatarId`.
 *
 * ⚠️ THE TELEGRAM DISPLAY NAME IS NOT ONE OF THOSE IDENTIFIERS AND MUST STAY ON THIS VIEW.
 * `displayNameOf` resolves nickname → `firstName [lastName]` → the app handle, so dropping
 * first/last does not make the view anonymous — it makes it fall through to the
 * auto-generated «bing…» HANDLE for every user who never chose a nickname, which on prod is
 * 99,333 of 101,605 people. `friendViewSchema`, the leaderboard row and the invite view have
 * always carried both fields for exactly this reason; this view was the lone omission.
 */
export const publicUserSchema = z.object({
  /** Stable internal app id (a cuid) — the id everything else keys on. */
  id: z.string(),
  /** Editable public handle (Telegram-style), or null if never set. */
  appUsername: z.string().nullable(),
  /** Display name. */
  nickname: z.string().nullable(),
  /** Telegram first name — NOT private; the fallback label when no `nickname` is set. */
  firstName: z.string().nullable().default(null),
  /** Telegram last name — NOT private; joined with `firstName` for the fallback name. */
  lastName: z.string().nullable().default(null),
  /** Preset avatar id (fallback when no photo is uploaded). */
  avatarId: z.string(),
  /** Uploaded avatar ref, or null. */
  avatarPhotoId: z.string().nullable(),
  /** Official/verified account (system + staff identities) → renders a badge. */
  isOfficial: z.boolean().default(false),
});
export type PublicUser = z.infer<typeof publicUserSchema>;

/**
 * The caller's own profile. Extends the public view with the private Telegram
 * fields (own view ONLY), the notifications preference, and stats. `telegramId`
 * and `username` (the Telegram username) must appear here and NOWHERE else.
 */
export const selfProfileSchema = publicUserSchema.extend({
  telegramId: z.string(),
  username: z.string().nullable(),
  // `firstName` / `lastName` are INHERITED from publicUserSchema — they are not private and
  // belong on both views. Re-declaring them here would be a second place to change.
  notificationsEnabled: z.boolean(),
  stats: userStatsSchema,
  /** Privacy toggles (own view) — whether OTHER users' profile view hides these sections. */
  hideHistory: z.boolean().default(false),
  hideStats: z.boolean().default(false),
  /** True ONLY on the caller's first-ever `GET /me` (brand-new account) — drives the
   *  one-time in-app welcome. Absent/false on every later launch. */
  isNew: z.boolean().optional(),
});
export type SelfProfile = z.infer<typeof selfProfileSchema>;

/**
 * Another user's full profile view — public identity + (unless the owner hid them)
 * aggregate stats/trophies and recent match history. A hidden section comes back as
 * `null` with its `*Hidden` flag true; the data is NEVER sent when hidden (enforced
 * server-side). The owner viewing their OWN profile sees everything (flags reflect their
 * settings but the data is still present).
 */
export const publicProfileSchema = z.object({
  user: publicUserSchema,
  stats: userStatsSchema.nullable(),
  history: matchHistoryPageSchema.nullable(),
  statsHidden: z.boolean(),
  historyHidden: z.boolean(),
});
export type PublicProfile = z.infer<typeof publicProfileSchema>;

/** Metadata returned after a successful avatar upload. */
export const avatarUploadResultSchema = z.object({
  avatarPhotoId: z.string(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  sizeBytes: z.number().int().positive(),
  mime: z.string(),
});
export type AvatarUploadResult = z.infer<typeof avatarUploadResultSchema>;
